import React, { useState, useEffect } from 'react';
import './App.css';

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });

  // Handle scroll detection for navbar glassmorphism effect
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (contactModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [contactModalOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ type: 'loading', message: 'Sending...' });
    
    // Simulate form submission
    setTimeout(() => {
      setFormStatus({ type: 'success', message: 'Thank you! We\'ll get back to you soon.' });
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setTimeout(() => {
        setContactModalOpen(false);
        setFormStatus({ type: '', message: '' });
      }, 2000);
    }, 1000);
  };

  const openContactModal = () => {
    setContactModalOpen(true);
    setMobileMenuOpen(false);
  };

  return (
    <div className="bg-background-light dark:bg-background-dark text-text-main dark:text-gray-100 font-display transition-colors duration-200">
      <div className="relative flex flex-col min-h-screen w-full overflow-x-hidden">
        {/* Sticky Navigation with Glassmorphism */}
        <nav 
          className={`fixed top-0 z-50 w-full transition-all duration-300 ease-in-out ${
            scrolled 
              ? 'bg-white/70 dark:bg-background-dark/70 backdrop-blur-lg shadow-lg border-b border-white/20' 
              : 'bg-transparent'
          }`}
          data-testid="navbar"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-20">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 text-primary flex items-center justify-center">
                  <span className="material-symbols-outlined text-3xl">explore</span>
                </div>
                <span className={`text-xl font-bold tracking-tight transition-colors duration-300 ${
                  scrolled ? 'text-text-main dark:text-white' : 'text-white'
                }`}>WayForm</span>
              </div>
              <div className="hidden md:flex items-center gap-8">
                <a 
                  className={`text-sm font-medium hover:text-primary transition-colors ${
                    scrolled ? 'text-text-main dark:text-gray-200' : 'text-gray-200'
                  }`} 
                  href="#features"
                >
                  Why WayForm?
                </a>
                <a 
                  className={`text-sm font-medium hover:text-primary transition-colors ${
                    scrolled ? 'text-text-main dark:text-gray-200' : 'text-gray-200'
                  }`} 
                  href="#how-it-works"
                >
                  How It Works
                </a>
              </div>
              <div className="flex items-center gap-4">
                <button 
                  className="hidden sm:flex bg-primary hover:bg-primary-dark text-text-main font-bold py-2 px-5 rounded-lg transition-colors text-sm shadow-sm"
                  data-testid="contact-us-nav-btn"
                  onClick={openContactModal}
                >
                  Contact Us
                </button>
                <button 
                  className={`md:hidden p-2 transition-colors duration-300 ${
                    scrolled ? 'text-text-main dark:text-white' : 'text-white'
                  }`}
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  data-testid="mobile-menu-btn"
                >
                  <span className="material-symbols-outlined">menu</span>
                </button>
              </div>
            </div>
            
            {/* Mobile Menu */}
            {mobileMenuOpen && (
              <div className={`md:hidden rounded-lg mt-2 p-4 transition-all duration-300 ${
                scrolled 
                  ? 'bg-white/80 dark:bg-background-dark/80 backdrop-blur-lg' 
                  : 'bg-black/90'
              }`} data-testid="mobile-menu">
                <a className={`block py-2 text-sm font-medium hover:text-primary transition-colors ${
                  scrolled ? 'text-text-main dark:text-gray-200' : 'text-gray-200'
                }`} href="#features" onClick={() => setMobileMenuOpen(false)}>Why WayForm?</a>
                <a className={`block py-2 text-sm font-medium hover:text-primary transition-colors ${
                  scrolled ? 'text-text-main dark:text-gray-200' : 'text-gray-200'
                }`} href="#how-it-works" onClick={() => setMobileMenuOpen(false)}>How It Works</a>
                <button 
                  className="mt-4 w-full bg-primary hover:bg-primary-dark text-text-main font-bold py-2 px-5 rounded-lg transition-colors text-sm shadow-sm"
                  onClick={openContactModal}
                >
                  Contact Us
                </button>
                </div>
              )}
            </div>
          </nav>

        {/* Header / Hero Section */}
        <header className="relative w-full h-[700px] flex items-center justify-center overflow-hidden">
          {/* Background Image */}
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/40 z-10"></div>
            <img 
              alt="Diverse group of university students smiling outdoors" 
              className="w-full h-full object-cover" 
              src="https://lh3.googleusercontent.com/aida-public/AB6AXuCyXcFVUVEBaxGiNy-OaDxdpVgD3K60BIcMSLHhwrMcKcqt92jARxl_2OIxIbnYEfBoncIEkyCq0YzbA4b1phCq01fLZlbDoTSTeCJ6G-Qjvr3BejZL67MA-5E66BIpuFqLPWm3qhLc12Rtmia5GAtCCVjj88hV144DUyoG-oqop0UgsClPdmpkW7qSzdiMek2R3nU2mIsdIm5Iv6R3kGi3KKUzaXCcjZg0-m1RmFIoGqHVkz5WSMQ_2N6eGJbpbqtphrw9J55iNB8"
            />
          </div>

          {/* Hero Content */}
          <div className="relative z-20 container mx-auto px-4 text-center mt-16">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-5xl md:text-7xl font-black text-white leading-tight mb-6 tracking-tight drop-shadow-lg">
                Don't Just Guess Your Future. <span className="text-primary">Chat With It.</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-100 font-light mb-10 max-w-2xl mx-auto leading-relaxed drop-shadow-md">
                Personalized academic and career guidance powered by AI, tailored just for your unique path.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  className="bg-primary hover:bg-primary-dark text-text-main text-xl font-bold py-4 px-10 rounded-lg shadow-xl transition-all transform hover:scale-105"
                  data-testid="hero-cta-btn"
                  onClick={openContactModal}
                >
                  Contact Us
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Why WayForm Section */}
        <section className="py-24 px-4 bg-background-light dark:bg-background-dark" id="features">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-text-main dark:text-white mb-6">Why WayForm?</h2>
              <p className="text-text-muted dark:text-gray-400 text-lg">Real answers to the questions keeping you up at night.</p>
            </div>
            <div className="space-y-6">
              {/* FAQ 1 */}
              <div className="bg-surface-light dark:bg-surface-dark p-8 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm" data-testid="faq-item-1">
                <h3 className="text-xl font-bold mb-3 text-text-main dark:text-white flex items-start gap-3">
                  <span className="text-primary material-symbols-outlined mt-1">help</span>
                  I'm overwhelmed by choices. Where do I start?
                </h3>
                <p className="text-text-muted dark:text-gray-400 leading-relaxed ml-9">
                  WayForm starts with <em>you</em>, not a massive database of colleges. We begin with a simple conversation about your interests and hobbies to find paths that genuinely fit your personality.
                </p>
              </div>
              
              {/* FAQ 2 */}
              <div className="bg-surface-light dark:bg-surface-dark p-8 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm" data-testid="faq-item-2">
                <h3 className="text-xl font-bold mb-3 text-text-main dark:text-white flex items-start gap-3">
                  <span className="text-primary material-symbols-outlined mt-1">help</span>
                  Is this just a fancy search engine?
                </h3>
                <p className="text-text-muted dark:text-gray-400 leading-relaxed ml-9">
                  Not at all. WayForm is an active guide. It understands context, remembers your preferences from previous chats, and builds a dynamic roadmap that evolves as you do.
                </p>
              </div>
              
              {/* FAQ 3 */}
              <div className="bg-surface-light dark:bg-surface-dark p-8 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm" data-testid="faq-item-3">
                <h3 className="text-xl font-bold mb-3 text-text-main dark:text-white flex items-start gap-3">
                  <span className="text-primary material-symbols-outlined mt-1">help</span>
                  Can I use this alongside my school counselor?
                </h3>
                <p className="text-text-muted dark:text-gray-400 leading-relaxed ml-9">
                  Absolutely. In fact, we recommend it. We handle the deep-dive research and initial exploration (the time-consuming part) so your meetings with human counselors can be focused on strategy and decision-making.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Chat Demo Section */}
        <section className="py-20 px-4 bg-white dark:bg-[#1a160e]">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              {/* Chat Interface */}
              <div className="relative order-2 lg:order-1">
                <div className="absolute -top-10 -left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl"></div>
                <div className="relative bg-white dark:bg-surface-dark rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-800 overflow-hidden max-w-md mx-auto lg:mx-0">
                  {/* Chat Header */}
                  <div className="bg-primary/10 px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-red-400"></div>
                    <div className="w-2 h-2 rounded-full bg-yellow-400"></div>
                    <div className="w-2 h-2 rounded-full bg-green-400"></div>
                    <span className="ml-2 text-xs font-semibold uppercase text-text-muted tracking-wider">WayForm AI</span>
                  </div>
                  
                  {/* Chat Messages */}
                  <div className="p-6 flex flex-col gap-6 h-[400px] overflow-y-auto bg-gray-50 dark:bg-[#252018] custom-scrollbar" data-testid="chat-demo">
                    {/* User Message 1 */}
                    <div className="flex justify-end">
                      <div className="bg-primary text-text-main px-4 py-3 rounded-2xl rounded-tr-none shadow-sm max-w-[85%] text-sm font-medium">
                        I'm really into graphic design, but I also love solving logic puzzles. What should I study?
                      </div>
                    </div>
                    
                    {/* AI Response 1 */}
                    <div className="flex justify-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                        <span className="material-symbols-outlined text-sm text-primary-dark">smart_toy</span>
                      </div>
                      <div className="bg-white dark:bg-surface-dark border border-gray-200 dark:border-gray-700 text-text-main dark:text-gray-200 px-4 py-3 rounded-2xl rounded-tl-none shadow-sm max-w-[90%] text-sm">
                        That's a fantastic combination! It suggests you might thrive in <strong>UX/UI Design</strong> or <strong>Frontend Development</strong>.
                        <br/><br/>
                        These fields blend creativity with problem-solving. Have you ever tried coding a simple website?
                      </div>
                    </div>
                    
                    {/* User Message 2 */}
                    <div className="flex justify-end">
                      <div className="bg-primary text-text-main px-4 py-3 rounded-2xl rounded-tr-none shadow-sm max-w-[85%] text-sm font-medium">
                        No, never tried coding. Is it hard to start?
                      </div>
                    </div>
                    
                    {/* AI Typing Indicator */}
                    <div className="flex justify-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                        <span className="material-symbols-outlined text-sm text-primary-dark">smart_toy</span>
                      </div>
                      <div className="bg-gray-200 dark:bg-gray-800 px-4 py-3 rounded-2xl rounded-tl-none flex gap-1 items-center h-10 w-16">
                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"></div>
                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce delay-75"></div>
                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce delay-150"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Chat Section Text */}
              <div className="flex flex-col justify-center order-1 lg:order-2">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary-dark text-sm font-bold w-fit mb-4">
                  <span className="material-symbols-outlined text-lg">chat_bubble</span>
                  Intelligent Chat
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-text-main dark:text-white mb-6">
                  It's like texting a friend who knows every college major.
                </h2>
                <p className="text-lg text-text-muted dark:text-gray-400 mb-8">
                  WayForm doesn't just give you a list of links. It has a conversation with you to understand who you really are, helping you uncover paths you never knew existed.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-20 px-4 bg-background-light dark:bg-background-dark" id="how-it-works">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-text-main dark:text-white mb-4">How WayForm Works</h2>
              <p className="text-text-muted dark:text-gray-400">From confusion to clarity in three simple steps.</p>
            </div>
            <div className="relative grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Connecting Line */}
              <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-0.5 border-t-2 border-dashed border-gray-300 dark:border-gray-700 -z-0"></div>
              
              {/* Step 1 */}
              <div className="relative flex flex-col items-center text-center z-10" data-testid="step-1">
                <div className="w-24 h-24 bg-white dark:bg-surface-dark border-4 border-background-light dark:border-background-dark rounded-full flex items-center justify-center shadow-lg mb-6">
                  <span className="material-symbols-outlined text-4xl text-primary">forum</span>
                </div>
                <h3 className="text-xl font-bold mb-2 dark:text-white">1. Chat & Explore</h3>
                <p className="text-text-muted dark:text-gray-400 text-sm max-w-xs">
                  Tell the AI about your hobbies, favorite classes, and what you hate doing.
                </p>
              </div>
              
              {/* Step 2 */}
              <div className="relative flex flex-col items-center text-center z-10" data-testid="step-2">
                <div className="w-24 h-24 bg-white dark:bg-surface-dark border-4 border-background-light dark:border-background-dark rounded-full flex items-center justify-center shadow-lg mb-6">
                  <span className="material-symbols-outlined text-4xl text-primary">psychology</span>
                </div>
                <h3 className="text-xl font-bold mb-2 dark:text-white">2. AI Analysis</h3>
                <p className="text-text-muted dark:text-gray-400 text-sm max-w-xs">
                  Our engine connects your profile to thousands of career paths and college programs.
                </p>
              </div>
              
              {/* Step 3 */}
              <div className="relative flex flex-col items-center text-center z-10" data-testid="step-3">
                <div className="w-24 h-24 bg-white dark:bg-surface-dark border-4 border-background-light dark:border-background-dark rounded-full flex items-center justify-center shadow-lg mb-6">
                  <span className="material-symbols-outlined text-4xl text-primary">near_me</span>
                </div>
                <h3 className="text-xl font-bold mb-2 dark:text-white">3. Get Your Plan</h3>
                <p className="text-text-muted dark:text-gray-400 text-sm max-w-xs">
                  Receive a personalized roadmap with recommended majors and next steps.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* A Day in the Life Section */}
        <section className="py-24 px-4 bg-white dark:bg-[#1a160e]">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row items-center gap-16">
              {/* Left Content */}
              <div className="w-full md:w-1/2">
                <div className="sticky top-24">
                  <h2 className="text-3xl md:text-5xl font-black text-text-main dark:text-white mb-6 leading-tight">
                    A Day in the Life<br/>
                    <span className="text-primary">of a Future Leader.</span>
                  </h2>
                  <p className="text-lg text-text-muted dark:text-gray-400 mb-8">
                    See how WayForm integrates seamlessly into your daily routine, turning anxiety into action.
                  </p>
                  <button className="hidden md:inline-flex items-center gap-2 text-primary font-bold hover:gap-4 transition-all" data-testid="start-journey-btn">
                    Start your journey <span className="material-symbols-outlined">arrow_forward</span>
                  </button>
                </div>
              </div>
              
              {/* Timeline */}
              <div className="w-full md:w-1/2 flex flex-col gap-8 relative">
                <div className="absolute left-8 top-8 bottom-8 w-0.5 bg-gray-100 dark:bg-gray-800"></div>
                
                {/* Timeline Item 1 */}
                <div className="relative pl-20" data-testid="timeline-1">
                  <div className="absolute left-0 w-16 h-16 bg-surface-light dark:bg-surface-dark border border-gray-100 dark:border-gray-800 rounded-full flex items-center justify-center z-10 shadow-sm">
                    <span className="material-symbols-outlined text-primary">wb_sunny</span>
                  </div>
                  <div className="pt-2">
                    <span className="text-xs font-bold tracking-widest uppercase text-text-muted mb-2 block">8:00 AM</span>
                    <h3 className="text-xl font-bold text-text-main dark:text-white mb-2">The Morning Doubt</h3>
                    <p className="text-text-muted dark:text-gray-400">
                      You wake up wondering if "Marine Biology" is actually a viable career or just a phase because you like the beach.
                    </p>
                  </div>
                </div>
                
                {/* Timeline Item 2 */}
                <div className="relative pl-20" data-testid="timeline-2">
                  <div className="absolute left-0 w-16 h-16 bg-surface-light dark:bg-surface-dark border border-gray-100 dark:border-gray-800 rounded-full flex items-center justify-center z-10 shadow-sm">
                    <span className="material-symbols-outlined text-primary">school</span>
                  </div>
                  <div className="pt-2">
                    <span className="text-xs font-bold tracking-widest uppercase text-text-muted mb-2 block">3:30 PM</span>
                    <h3 className="text-xl font-bold text-text-main dark:text-white mb-2">The Insight</h3>
                    <p className="text-text-muted dark:text-gray-400">
                      On the bus home, you chat with WayForm. It analyzes your skills and suggests "Environmental Science" as a broader alternative with more job stability.
                    </p>
                  </div>
                </div>
                
                {/* Timeline Item 3 */}
                <div className="relative pl-20" data-testid="timeline-3">
                  <div className="absolute left-0 w-16 h-16 bg-surface-light dark:bg-surface-dark border border-gray-100 dark:border-gray-800 rounded-full flex items-center justify-center z-10 shadow-sm">
                    <span className="material-symbols-outlined text-primary">bedtime</span>
                  </div>
                  <div className="pt-2">
                    <span className="text-xs font-bold tracking-widest uppercase text-text-muted mb-2 block">8:00 PM</span>
                    <h3 className="text-xl font-bold text-text-main dark:text-white mb-2">The Action Plan</h3>
                    <p className="text-text-muted dark:text-gray-400">
                      You add 3 target universities to your dashboard. WayForm instantly generates a list of prerequisite courses you need to take next year. You sleep soundly.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 px-4 bg-background-light dark:bg-background-dark text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-black text-text-main dark:text-white mb-6">Ready to plan your future?</h2>
            <p className="text-xl text-text-muted dark:text-gray-300 mb-10 max-w-2xl mx-auto">
              Join over 5,000 students who have found their path with WayForm. Start your free discovery session today.
            </p>
            <button 
              className="bg-primary hover:bg-primary-dark text-text-main text-lg font-bold py-4 px-10 rounded-lg shadow-xl hover:shadow-2xl transition-all transform hover:-translate-y-1 mb-12"
              data-testid="cta-contact-btn"
              onClick={openContactModal}
            >
              Contact Us
            </button>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-white dark:bg-[#15120b] border-t border-gray-100 dark:border-gray-800 py-12 px-4">
          <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            {/* Logo Column */}
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <span className="material-symbols-outlined text-primary text-2xl">explore</span>
                <span className="text-lg font-bold text-text-main dark:text-white">WayForm</span>
              </div>
              <p className="text-sm text-text-muted dark:text-gray-500">
                Empowering the next generation to make confident decisions about their education and careers.
              </p>
            </div>
            
            {/* Product Links */}
            <div>
              <h4 className="font-bold text-text-main dark:text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm text-text-muted dark:text-gray-400">
                <li><a className="hover:text-primary transition-colors" href="#">Features</a></li>
                <li><a className="hover:text-primary transition-colors" href="#">For Schools</a></li>
              </ul>
            </div>
            
            {/* Company Links */}
            <div>
              <h4 className="font-bold text-text-main dark:text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-text-muted dark:text-gray-400">
                <li><a className="hover:text-primary transition-colors" href="#">About Us</a></li>
                <li><a className="hover:text-primary transition-colors" href="#">Careers</a></li>
                <li><a className="hover:text-primary transition-colors" href="#">Blog</a></li>
              </ul>
            </div>
            
            {/* Support Links */}
            <div>
              <h4 className="font-bold text-text-main dark:text-white mb-4">Support</h4>
              <ul className="space-y-2 text-sm text-text-muted dark:text-gray-400">
                <li><a className="hover:text-primary transition-colors" href="#">Help Center</a></li>
                <li><a className="hover:text-primary transition-colors" href="#">Contact Us</a></li>
                <li><a className="hover:text-primary transition-colors" href="#">Privacy Policy</a></li>
              </ul>
            </div>
          </div>
          
          {/* Footer Bottom */}
          <div className="max-w-7xl mx-auto pt-8 border-t border-gray-100 dark:border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-text-muted dark:text-gray-600">
            <p>© 2025 WayForm Inc. All rights reserved.</p>
            <div className="flex gap-4">
              <a className="hover:text-text-main dark:hover:text-white transition-colors" href="#">Terms</a>
              <a className="hover:text-text-main dark:hover:text-white transition-colors" href="#">Privacy</a>
              <a className="hover:text-text-main dark:hover:text-white transition-colors" href="#">Cookies</a>
            </div>
          </div>
        </footer>
      </div>

      {/* Contact Us Modal */}
      {contactModalOpen && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          data-testid="contact-modal"
        >
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setContactModalOpen(false)}
          ></div>
          
          {/* Modal Content */}
          <div className="relative bg-white dark:bg-surface-dark rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-modal-in">
            {/* Close Button */}
            <button 
              onClick={() => setContactModalOpen(false)}
              className="absolute top-4 right-4 p-2 text-text-muted hover:text-text-main dark:hover:text-white transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
              data-testid="close-modal-btn"
            >
              <span className="material-symbols-outlined">close</span>
            </button>
            
            {/* Modal Header */}
            <div className="p-6 pb-4 border-b border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <span className="material-symbols-outlined text-primary">mail</span>
                </div>
                <h2 className="text-2xl font-bold text-text-main dark:text-white">Contact Us</h2>
              </div>
              <p className="text-text-muted dark:text-gray-400 text-sm">
                Have questions about WayForm? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
              </p>
            </div>
            
            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              {/* Name Field */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-text-main dark:text-gray-200 mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="John Doe"
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-background-dark text-text-main dark:text-white placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  data-testid="contact-name-input"
                />
              </div>
              
              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-text-main dark:text-gray-200 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="john@example.com"
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-background-dark text-text-main dark:text-white placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  data-testid="contact-email-input"
                />
              </div>
              
              {/* Phone Field */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-text-main dark:text-gray-200 mb-2">
                  Phone Number <span className="text-text-muted">(Optional)</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+1 (555) 000-0000"
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-background-dark text-text-main dark:text-white placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  data-testid="contact-phone-input"
                />
              </div>
              
              {/* Subject Dropdown */}
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-text-main dark:text-gray-200 mb-2">
                  Subject <span className="text-red-500">*</span>
                </label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-background-dark text-text-main dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  data-testid="contact-subject-select"
                >
                  <option value="">Select a subject...</option>
                  <option value="general">General Inquiry</option>
                  <option value="demo">Request a Demo</option>
                  <option value="pricing">Pricing Information</option>
                  <option value="schools">For Schools / Institutions</option>
                  <option value="support">Technical Support</option>
                  <option value="partnership">Partnership Opportunities</option>
                </select>
              </div>
              
              {/* Message Field */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-text-main dark:text-gray-200 mb-2">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  placeholder="Tell us how we can help you..."
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-background-dark text-text-main dark:text-white placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-none"
                  data-testid="contact-message-input"
                ></textarea>
              </div>
              
              {/* Status Message */}
              {formStatus.message && (
                <div className={`p-4 rounded-lg flex items-center gap-3 ${
                  formStatus.type === 'success' 
                    ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400' 
                    : formStatus.type === 'error'
                    ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
                    : 'bg-primary/10 text-primary-dark'
                }`}>
                  <span className="material-symbols-outlined text-xl">
                    {formStatus.type === 'success' ? 'check_circle' : formStatus.type === 'error' ? 'error' : 'hourglass_empty'}
                  </span>
                  {formStatus.message}
                </div>
              )}
              
              {/* Submit Button */}
              <button
                type="submit"
                disabled={formStatus.type === 'loading'}
                className="w-full bg-primary hover:bg-primary-dark disabled:bg-primary/50 text-text-main font-bold py-4 px-6 rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5 disabled:transform-none disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="contact-submit-btn"
              >
                {formStatus.type === 'loading' ? (
                  <>
                    <span className="material-symbols-outlined animate-spin">progress_activity</span>
                    Sending...
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined">send</span>
                    Send Message
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
